import { motion } from "framer-motion";
import { Radar, BrainCircuit, MapPinned, LineChart, ShieldCheck, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const PILLARS = [
  { icon: Radar, title: "Precision Geofencing", text: "Geofence zones from 100m to 1km around strategically selected physical locations." },
  { icon: MapPinned, title: "Geolocation Intelligence", text: "Understand where your audiences are and how they move around your locations." },
  { icon: Zap, title: "Real-Time Activation", text: "Campaign events stream live into dashboards the moment they happen." },
  { icon: LineChart, title: "Campaign Analytics", text: "Impressions, reach, engagement, clicks and video metrics per location." },
  { icon: BrainCircuit, title: "AI-Driven Insights", text: "Intelligent performance summaries and optimisation-ready data models." },
  { icon: ShieldCheck, title: "Privacy First", text: "POPIA-aligned, consent-based, aggregated analytics with anonymous identifiers." },
];

export default function About() {
  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(29,78,216,0.15),transparent_50%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <p className="section-label">About GEOTRAQ AI</p>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-white tracking-tight leading-tight">
              LOCATION INTELLIGENCE MEETS <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">DIGITAL ADVERTISING</span>
            </h1>
            <p className="mt-6 text-lg text-slate-400 leading-relaxed">
              GEOTRAQ AI combines geofencing, geolocation, digital advertising, location intelligence,
              campaign analytics, interactive mapping, real-time reporting and AI-driven insights into one
              enterprise advertising technology platform.
            </p>
            <p className="mt-4 text-slate-400 leading-relaxed">
              We help brands understand where their audiences are and deliver relevant campaigns around
              strategically selected physical locations — from transport hubs and stadiums to shopping
              malls and retail precincts. GEOTRAQ AI positions itself as the world's first interactive
              geofencing and geolocation advertising platform, built for enterprises that demand precision
              and accountability.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => (
            <motion.div key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="card-surface p-7 hover:border-cyan-500/30 transition-colors">
              <p.icon className="w-7 h-7 text-cyan-400" />
              <h3 className="mt-5 font-display font-semibold text-white">{p.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="font-display text-3xl font-bold text-white tracking-tight">Ready to activate your locations?</h2>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <Link to="/contact"><Button size="lg" className="bg-blue-600 hover:bg-blue-500 font-semibold px-8" data-testid="about-cta">TALK TO OUR TEAM</Button></Link>
            <Link to="/how-it-works"><Button size="lg" variant="outline" className="border-white/15 text-white hover:bg-white/5 px-8">See How It Works</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
